import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Award, Building2, CheckCircle2, Compass, Factory,
  FileSignature, Hammer, Home as HomeIcon, Package, Phone, ShieldCheck,
  Sofa, Star,
} from "lucide-react";
import { Reveal } from "../components/Reveal";
import {
  COMPANY, FEATURES_INCLUDED, PACKAGES, SERVICES, STATS, TESTIMONIALS,
} from "../lib/constants";

const ICONS = { Hammer, Package, Home: HomeIcon, Building2, Sofa, Factory, FileSignature, Compass };

export default function Home() {
  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section className="relative" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
            alt="Modern luxury residential tower"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative container-x min-h-[88vh] flex flex-col justify-end pb-20 pt-32 md:pb-28">
          <div className="max-w-3xl">
            <span className="inline-block text-[var(--m-gold)] text-xs tracking-[0.32em] uppercase font-medium" data-testid="hero-eyebrow">
              {COMPANY.tagline}
            </span>
            <h1
              className="font-display text-white mt-6 text-5xl md:text-7xl leading-[1.02] tracking-tight"
              data-testid="hero-title"
            >
              From Drawing to <em className="text-[var(--m-gold)] not-italic font-normal italic">Dream Home</em> —<br />
              We Build Everything.
            </h1>
            <p className="mt-8 text-white/85 text-lg md:text-xl max-w-2xl leading-relaxed" data-testid="hero-sub">
              An engineer-led, government-trusted construction firm. Residential, commercial, interiors,
              PEB and approval-grade drawings — delivered turnkey across Bengaluru.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/quotation" className="btn-gold" data-testid="hero-cta-quote">
                Get Quote <ArrowRight size={16} />
              </Link>
              <a href={`tel:${COMPANY.phoneRaw}`} className="btn-secondary" style={{ color: "#FDFBF7", borderColor: "#FDFBF7" }} data-testid="hero-cta-call">
                <Phone size={15} /> Free Consultation
              </a>
              <a
                href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent("I'd like to book a site visit")}`}
                target="_blank" rel="noopener noreferrer"
                className="btn-secondary"
                style={{ color: "#FDFBF7", borderColor: "rgba(253,251,247,0.4)" }}
                data-testid="hero-cta-visit"
              >
                Book Site Visit
              </a>
            </div>
          </div>
        </div>
        {/* trust strip */}
        <div className="relative bg-[var(--m-ink)] text-white/85">
          <div className="container-x py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center md:text-left">
            {[
              ["Chartered Engineer", "C.Eng (Civil)"],
              ["Registered BBMP Engineer", "Govt. Recognised"],
              ["Govt. of Karnataka", "RGHCL — since 2018"],
              ["Single-Engineer Accountability", "No Hand-off Chaos"],
            ].map(([h, s]) => (
              <div key={h} className="flex items-center gap-3 justify-center md:justify-start" data-testid={`trust-${h.toLowerCase().replace(/\s+/g, "-")}`}>
                <ShieldCheck size={18} className="text-[var(--m-gold)]" strokeWidth={1.6} />
                <div>
                  <div className="text-sm font-semibold">{h}</div>
                  <div className="text-xs text-white/60">{s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="section-pad bg-[var(--m-bg)]" data-testid="founder-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <Reveal className="lg:col-span-5">
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/8961031/pexels-photo-8961031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=800"
                alt="Mr. Kiran K examining structural drawings"
                className="w-full h-[520px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 hidden md:flex items-center gap-3 bg-[var(--m-bg)] border border-[var(--m-border)] px-6 py-5">
                <Award size={28} className="text-[var(--m-gold)]" strokeWidth={1.4} />
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--m-ink-3)]">Since</div>
                  <div className="font-display text-2xl text-[var(--m-ink)]">2018</div>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-7">
            <span className="eyebrow" data-testid="founder-eyebrow">The Engineer Behind Agartha</span>
            <div className="gold-divider mt-4" />
            <h2 className="font-display text-4xl md:text-5xl mt-6 leading-[1.05] tracking-tight" data-testid="founder-name">
              Mr. Kiran K, <span className="italic text-[var(--m-teal)]">C.Eng (Civil)</span>
            </h2>
            <p className="mt-3 text-[var(--m-ink-2)] text-lg">
              Registered BBMP Engineer • B.E. Civil, UVCE Bangalore
            </p>
            <p className="mt-8 text-[var(--m-ink-2)] leading-relaxed">
              For over 8 years, Mr. Kiran has been an Assistant Engineer with{" "}
              <strong className="text-[var(--m-ink)]">Rajiv Gandhi Housing Corporation Limited</strong>
              {" "}(Government of Karnataka Undertaking), executing the landmark <em>Bengaluru Housing Programme</em>.
              From G+3 to S+14 apartments, he has personally supervised drawings, approvals, structural design,
              and on-site execution using MIVAN, Shear Wall and Monolithic technologies.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4">
              {[
                "Detailed Project Reports (DPR)",
                "BBMP Plan Sanctions",
                "AUTOCAD • REVIT • TEKLA",
                "MIVAN • Shear Wall • Monolithic",
                "Raft / Pile / Strip / Combined Foundations",
                "Govt. Quality Control Standards",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3 py-2 border-b border-[var(--m-border)]">
                  <CheckCircle2 size={16} className="mt-1 text-[var(--m-teal)]" strokeWidth={1.6} />
                  <span className="text-sm text-[var(--m-ink)]">{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/about" className="btn-primary" data-testid="founder-about-cta">
                Read Full Story <ArrowRight size={15} />
              </Link>
              <a href={`tel:${COMPANY.phoneRaw}`} className="btn-secondary" data-testid="founder-call-cta">
                <Phone size={15} /> Speak to Kiran
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[var(--m-ink)] text-white" data-testid="stats-section">
        <div className="container-x py-20 grid grid-cols-2 md:grid-cols-3 gap-8">
          {STATS.map((s) => (
            <div key={s.label} className="text-left" data-testid={`stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="font-display text-5xl md:text-6xl text-[var(--m-gold)]">{s.value}</div>
              <div className="mt-3 text-xs tracking-[0.2em] uppercase text-white/70">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="section-pad" data-testid="services-section">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
            <Reveal className="lg:col-span-5">
              <span className="eyebrow">What We Build</span>
              <div className="gold-divider mt-4" />
              <h2 className="font-display text-4xl md:text-5xl mt-6 leading-[1.05]">
                One firm. <em className="italic text-[var(--m-teal)]">Every</em> construction need.
              </h2>
            </Reveal>
            <Reveal delay={100} className="lg:col-span-6 lg:col-start-7">
              <p className="text-[var(--m-ink-2)] leading-relaxed text-lg">
                Eight specialised verticals under a single engineer-led roof — so you never juggle
                contractors, draftsmen, approval consultants and interior firms again.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" style={{ counterReset: "svc-counter" }}>
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon];
              return (
                <Reveal key={s.key} delay={i * 60}>
                  <Link to="/services" className="card-flat p-8 h-full flex flex-col group" data-testid={`service-card-${s.key}`}>
                    <div className="flex items-start justify-between">
                      <div className="numbered" />
                      <Icon size={28} className="text-[var(--m-teal)] transition-transform group-hover:scale-110" strokeWidth={1.4} />
                    </div>
                    <h3 className="font-display text-2xl mt-6 leading-tight">{s.title}</h3>
                    <p className="mt-4 text-sm text-[var(--m-ink-2)] leading-relaxed flex-1">{s.desc}</p>
                    <span className="mt-6 text-xs tracking-[0.18em] uppercase text-[var(--m-teal)] flex items-center gap-2 group-hover:gap-3 transition-all">
                      Explore <ArrowRight size={13} />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING / PACKAGES */}
      <section className="section-pad bg-[var(--m-bg-2)]" data-testid="packages-section">
        <div className="container-x">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="eyebrow">Transparent Per Sq.Ft. Packages</span>
            <div className="gold-divider mt-4 mx-auto" />
            <h2 className="font-display text-4xl md:text-5xl mt-6">Three packages. Zero hidden costs.</h2>
            <p className="mt-5 text-[var(--m-ink-2)] leading-relaxed">
              Branded materials, exact rates per bag and per tonne, drawings included.
              Pick a package — or talk to us for a custom one.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {PACKAGES.map((p, idx) => (
              <Reveal key={p.key} delay={idx * 80}>
                <div
                  className="bg-[var(--m-bg)] border p-10 flex flex-col h-full relative"
                  style={{
                    borderColor: p.highlight ? "var(--m-teal)" : "var(--m-border)",
                    boxShadow: p.highlight ? "0 30px 60px -30px rgba(31,78,91,0.35)" : "none",
                  }}
                  data-testid={`package-${p.key}`}
                >
                  {p.highlight && (
                    <span className="absolute -top-3 left-10 tag tag-teal bg-[var(--m-bg)]">Most Popular</span>
                  )}
                  <div className="text-xs tracking-[0.22em] uppercase text-[var(--m-ink-3)]">{p.name} Package</div>
                  <div className="mt-6 flex items-baseline gap-2">
                    <span className="font-display text-6xl text-[var(--m-ink)]">₹{p.price}</span>
                    <span className="text-sm text-[var(--m-ink-3)]">/ sq.ft.</span>
                  </div>
                  <div className="mt-2 text-xs text-[var(--m-ink-3)]">{p.sub}</div>
                  <div className="divider-h my-8" />
                  <ul className="flex flex-col gap-3 flex-1">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-[var(--m-ink-2)]">
                        <CheckCircle2 size={15} className="mt-0.5 text-[var(--m-teal)] flex-shrink-0" strokeWidth={1.6} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/quotation?package=${p.key}`}
                    className={p.highlight ? "btn-primary mt-10 justify-center" : "btn-secondary mt-10 justify-center"}
                    data-testid={`package-cta-${p.key}`}
                  >
                    Choose {p.name}
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-5 gap-4">
            {FEATURES_INCLUDED.map((f) => (
              <div key={f} className="text-xs tracking-wide text-[var(--m-ink-2)] flex items-center gap-2 py-2 border-b border-[var(--m-border)]">
                <CheckCircle2 size={13} className="text-[var(--m-gold)]" strokeWidth={1.8} />
                {f}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="section-pad" data-testid="why-section">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-16">
          <Reveal className="lg:col-span-5">
            <span className="eyebrow">Why Agartha</span>
            <div className="gold-divider mt-4" />
            <h2 className="font-display text-4xl md:text-5xl mt-6 leading-[1.05]">
              Not just another contractor. <em className="italic text-[var(--m-teal)]">An engineer-led firm.</em>
            </h2>
            <p className="mt-6 text-[var(--m-ink-2)] leading-relaxed">
              Most builders sub-contract drawings, approvals and structural work to three different
              vendors. We don't. Mr. Kiran personally signs off on every drawing, every approval and
              every concrete pour — the same standards he applies to government housing projects.
            </p>
          </Reveal>
          <Reveal delay={100} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[var(--m-border)]">
            {[
              ["Government-Grade Quality", "We follow the same IS-code QA/QC processes used on the 1L Bengaluru Housing Programme."],
              ["Single Point of Contact", "One engineer, one contract, one accountable team — from soil report to handover."],
              ["Transparent Material Pricing", "Brand names, bag rates and per-tonne rates printed in every quotation."],
              ["BBMP Compliance", "Approvals handled in-house by a Registered BBMP Engineer."],
            ].map(([t, d]) => (
              <div key={t} className="bg-[var(--m-bg)] p-8" data-testid={`why-${t.toLowerCase().replace(/\s+/g, "-")}`}>
                <Star size={18} className="text-[var(--m-gold)] mb-4" strokeWidth={1.5} />
                <h3 className="font-display text-2xl">{t}</h3>
                <p className="mt-3 text-sm text-[var(--m-ink-2)] leading-relaxed">{d}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-pad bg-[var(--m-bg-2)]" data-testid="testimonials-section">
        <div className="container-x">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <span className="eyebrow">Voices of Trust</span>
              <div className="gold-divider mt-4" />
              <h2 className="font-display text-4xl md:text-5xl mt-6">What our clients say.</h2>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={18} className="fill-[var(--m-gold)] text-[var(--m-gold)]" />
                ))}
              </div>
              <span className="text-sm text-[var(--m-ink-2)]">5.0 average across Google reviews</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.name} delay={i * 60}>
                <div className="bg-[var(--m-bg)] border border-[var(--m-border)] p-8 h-full flex flex-col" data-testid={`testimonial-${i}`}>
                  <div className="flex">
                    {[...Array(t.rating)].map((_, k) => (
                      <Star key={k} size={15} className="fill-[var(--m-gold)] text-[var(--m-gold)]" />
                    ))}
                  </div>
                  <p className="mt-6 text-[var(--m-ink-2)] leading-relaxed italic">"{t.quote}"</p>
                  <div className="mt-auto pt-6 border-t border-[var(--m-border)]">
                    <div className="font-semibold text-[var(--m-ink)]">{t.name}</div>
                    <div className="text-xs text-[var(--m-ink-3)] mt-1">{t.role}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-[var(--m-teal)] text-white" data-testid="bottom-cta">
        <div className="container-x py-20 grid grid-cols-1 md:grid-cols-12 items-center gap-10">
          <div className="md:col-span-7">
            <span className="text-[var(--m-gold)] text-xs tracking-[0.32em] uppercase">Ready to begin?</span>
            <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[1.05]">
              Get a transparent quotation in 24 hours.
            </h2>
            <p className="mt-4 text-white/80 max-w-xl">
              Tell us your plot size, location and package preference — we'll send a line-item estimate
              with material specs and timeline.
            </p>
          </div>
          <div className="md:col-span-5 flex flex-wrap gap-4 md:justify-end">
            <Link to="/quotation" className="btn-gold" data-testid="bottom-cta-quote">Get Free Quotation</Link>
            <a
              href={`https://wa.me/${COMPANY.whatsapp}`}
              target="_blank" rel="noopener noreferrer"
              className="btn-secondary"
              style={{ color: "#FDFBF7", borderColor: "#FDFBF7" }}
              data-testid="bottom-cta-whatsapp"
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
